import { DecisionReason } from "../../../../../../model/model"
import EvaluateRequest from "../../../../../EvaluateRequest"
import { EvaluatorContext } from "../../../../../evaluator/Evaluator"
import EvaluationEventRecorder from "../../../../../event/EvaluationEventRecorder"
import RemoteEvaluator from "../../../../../mode/remote/RemoteEvaluator"
import { InAppMessageEligibilityEvaluateResult } from "../../InAppMessageEligibilityEvaluateResult"
import { InAppMessageEligibilityEvaluateResponse } from "../../InAppMessageEligibilityEvaluateResponse"
import { InAppMessageEligibilityRemoteEvaluateRequest } from "./InAppMessageEligibilityRemoteEvaluateRequest"
import InAppMessageEligibilityRemoteEvaluationFlowFactory from "../../flow/InAppMessageEligibilityRemoteEvaluationFlowFactory"
import InAppMessageEligibilityEvaluator from "../../InAppMessageEligibilityEvaluator"

/**
 * Evaluates eligibility from a server-resolved result. The base evaluator resolves the result's
 * references into the context first, then the flow re-applies the client-side gates
 * (platform/period/timetable/deduplication) on top of the server decision.
 */
export class InAppMessageEligibilityRemoteEvaluator
  extends RemoteEvaluator<InAppMessageEligibilityRemoteEvaluateRequest, InAppMessageEligibilityEvaluateResponse>
  implements InAppMessageEligibilityEvaluator<InAppMessageEligibilityRemoteEvaluateRequest>
{
  constructor(
    private readonly evaluationFlowFactory: InAppMessageEligibilityRemoteEvaluationFlowFactory,
    private readonly eventRecorder: EvaluationEventRecorder
  ) {
    super()
  }

  /**
   * @override
   */
  supports(request: EvaluateRequest): request is InAppMessageEligibilityRemoteEvaluateRequest {
    return request instanceof InAppMessageEligibilityRemoteEvaluateRequest
  }

  /**
   * @override
   */
  protected remoteEvaluate(
    request: InAppMessageEligibilityRemoteEvaluateRequest,
    context: EvaluatorContext
  ): InAppMessageEligibilityEvaluateResponse {
    const evaluationFlow = this.evaluationFlowFactory.get(request)
    const result =
      evaluationFlow.evaluate(request, context) ??
      InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.NOT_IN_IN_APP_MESSAGE_TARGET)
    return InAppMessageEligibilityEvaluateResponse.of(request, context, result)
  }

  /**
   * @override
   */
  record(
    _request: InAppMessageEligibilityRemoteEvaluateRequest,
    response: InAppMessageEligibilityEvaluateResponse
  ): void {
    this.eventRecorder.record(response)

    // evaluation which is ineligible with nonnullable layout evaluation means that message is deduplicated or hidden.
    // for preventing imbalance, we should record layout evaluation tracking exposure event.
    if (!response.evaluation.isEligible && response.layout !== null) {
      this.eventRecorder.record(response.layout)
    }
  }
}
